import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { plural } from '../../../../../shared/helpers';
import Locator from '../../../../../shared/Locator';

type ReasonSegment = { text: string; isBold: boolean };

const blockedPackageReason: ReasonSegment[] = [{
  text: 'This version of the package has been blocked.',
  isBold: false,
}];

/**
 * Displays the issue details for a non-risk Quality issue.
 */
const BaseQualityDescription: VoidFunctionComponent = () => {
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails);

  if (!issueDetails) {
    return null;
  }

  let reasonSegments: ReasonSegment[] = [];

  if (issueDetails.type === 'blacklisted_dependency') {
    reasonSegments = blockedPackageReason;
  } else if (issueDetails.type === 'outdated_dependency') {
    const { qualityRule } = issueDetails;
    if (qualityRule?.dependencyOutdatedVersionDiff && qualityRule?.dependencyOutdatedVersionPart) {
      reasonSegments = [
        { text: 'This version is at least ', isBold: false },
        {
          text: `${qualityRule.dependencyOutdatedVersionDiff} ${qualityRule.dependencyOutdatedVersionPart}`.toLowerCase(),
          isBold: true,
        },
        { text: ` ${plural(qualityRule.dependencyOutdatedVersionDiff, 'version')} behind the latest version`, isBold: false },
      ];

      if (issueDetails?.latestVersion) {
        const { revision } = Locator.parse(issueDetails.latestVersion);
        if (revision) {
          reasonSegments.push(
            { text: ', which is ', isBold: false },
            { text: revision, isBold: true },
          );
        }
      }

      reasonSegments.push(
        //  Add the final period, which should appear unbolded
        { text: '.', isBold: false },
      );
    }
  }

  return reasonSegments.length > 0 ? (
    <div>
      {reasonSegments.map((segment) =>
        segment.isBold ? (
          <span className="bold" key={segment.text}>
            {segment.text}
          </span>
        ) : (
          <span key={segment.text}>{segment.text}</span>
        ))}
    </div>
  ) : null;
};

export default BaseQualityDescription;
